import { useLocation, useNavigate } from "react-router-dom";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { useGetNewsByID } from "../../domain/hooks/get_news_by_id";
import { useEffect, useMemo, useRef, useState } from "react";
import { SyncLoader } from "react-spinners";
import ReactMarkdown from "react-markdown";
import { markdownComponents } from '../../../course/presentation/components/markdown_components';
import BackArrowIcon from '../../../../assets/images/back_arrow_icon.svg?react';
import { useTranslation } from '../../../../i18n/useTranslation';

const ReadNewsPage: React.FC = () => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const location = useLocation();
    const navigate = useNavigate();
    const [imageFit, setImageFit] = useState(false);
    const imageContainerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [imgNatural, setImgNatural] = useState<{ w: number; h: number } | null>(null);

    const newsId = useMemo(() => {
        const urlParts = location.pathname.split('/');
        return urlParts[urlParts.length - 1];
    }, [location.pathname]);

    const {
        data: news,
        isLoading,
        error,
        refetch
    } = useGetNewsByID(newsId, location.state?.news);

    useEffect(() => {
        const el = imageContainerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect.width ?? 0;
            setContainerWidth(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [news?.picture]);

    const cropOccurs = useMemo(() => {
        if (!imgNatural || containerWidth === 0) return true;
        const containerRatio = containerWidth / 400;
        const imgRatio = imgNatural.w / imgNatural.h;
        return Math.abs(imgRatio - containerRatio) > 0.02;
    }, [imgNatural, containerWidth]);

    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = '#FFFFFF';
        return () => { document.body.style.backgroundColor = prev; };
    }, []);

    return (
      <div className="min-h-dvh w-full flex flex-col relative items-center justify-center">
            <header className="rounded-full absolute top-8 start-5 p-1.5 cursor-pointer z-10 shadow-md" style={{ backgroundColor: theme.UvibesPink }} onClick={() => navigate(-1)}>
                <BackArrowIcon width={20} height={20} />
            </header>
            {isLoading
                ? <SyncLoader color={theme.UvibesPink} size={24} speedMultiplier={0.5}/>
                : error !== null
                    ? <div className="flex flex-col items-center gap-5">
                        <p className="font-semibold text-center text-sm">{t('news.fetch_one_error')}</p>
                        <button
                            className="rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 cursor-pointer"
                            style={{ backgroundColor: theme.UvibesPink }}
                            onClick={() => refetch()}
                        >
                            {t('common.button.retry')}
                        </button>
                    </div>
                    : <>
                        {news?.picture && (
                            <div
                                ref={imageContainerRef}
                                className={`relative w-full bg-white overflow-hidden ${cropOccurs ? 'cursor-pointer' : ''}`}
                                onClick={() => { if (cropOccurs) setImageFit(prev => !prev); }}
                            >
                                <img
                                    key={imageFit ? 'fit' : 'cover'}
                                    src={news!.picture}
                                    alt={t('news.picture_alt')}
                                    onLoad={(e) => {
                                        const img = e.currentTarget;
                                        setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
                                    }}
                                    className={`w-full h-100 animate-news-image-fade ${imageFit ? "object-contain" : "object-cover object-top"}`}
                                />
                                {cropOccurs && <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setImageFit(prev => !prev); }}
                                    className="absolute top-8 right-5 rounded-full p-2 cursor-pointer shadow-md flex items-center justify-center transition-all duration-300 ease-in-out hover:scale-110 active:scale-95"
                                    style={{ backgroundColor: theme.UvibesPink }}
                                    aria-label={imageFit ? t('news.image_fill') : t('news.image_fit')}
                                >
                                    <div className={`transition-transform duration-300 ease-in-out ${imageFit ? 'rotate-180' : 'rotate-0'}`}>
                                        {imageFit ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M4 14h6v6" />
                                                <path d="M20 10h-6V4" />
                                                <path d="M14 10l7-7" />
                                                <path d="M3 21l7-7" />
                                            </svg>
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M15 3h6v6" />
                                                <path d="M9 21H3v-6" />
                                                <path d="M21 3l-7 7" />
                                                <path d="M3 21l7-7" />
                                            </svg>
                                        )}
                                    </div>
                                </button>}
                            </div>
                        )}
                        <div className="bg-white w-full flex-1 px-3 py-4 flex flex-col items-center gap-8 overflow-x-clip relative">
                            <div className="bg-white w-full h-7.5 rounded-t-[30px] absolute -top-7.5"/>
                            {news !== undefined && (
                                <>
                                    <h1 className="font-bold text-3xl text-center">{news.title}</h1>
                                    <div className="prose prose-lg max-w-153">
                                        <ReactMarkdown
                                            components={markdownComponents}
                                        >
                                            {news.text}
                                        </ReactMarkdown>
                                    </div>
                                </>
                            )}
                        </div>
                    </>
            }

      </div>
    );
};

export default ReadNewsPage;